/**
 * Mettler Toledo MT-SICS (and Kern KCP, which answers SI with an "SI" prefix): `S S      100.00 g` stable,
 * `S D      129.07 g` dynamic, `S +` / `S -` overload / underload, `I4 A "B123456789"` serial number.
 */
import type { BalanceEvent, BalanceProtocol } from "../types";
import { toGrams } from "./units";

const READING = /^(S|SI|SIR)\s+([SD])\s+([+-]?)\s*([\d.]+)\s*(\S*)\s*$/;
const RANGE = /^(S|SI)\s+([+-])\s*$/;
const IDENTITY = /^I([24])\s+A\s+"?(.*?)"?\s*$/;
const ACKS = /^(T|TI|TAC|Z|ZI|@)\s+[SA]\b|^S\s+I\s*$|^I\d\s+A\b/;
const ERRORS: Record<string, string> = {
  ES: "The balance did not understand the command",
  ET: "The balance reported a transmission error",
  EL: "The balance could not carry out the command",
};

export function parseMtSicsLine(line: string, at: number): BalanceEvent | null {
  const trimmed = line.trim();
  const reading = READING.exec(trimmed);
  if (reading) {
    const [, , flag, sign = "", digits = "", unit = ""] = reading;
    return {
      type: "reading",
      reading: { grams: toGrams(sign, digits, unit), unit, stable: flag === "S", overload: false, raw: line, at },
    };
  }
  if (RANGE.test(trimmed)) {
    return { type: "reading", reading: { grams: null, unit: "", stable: null, overload: true, raw: line, at } };
  }
  const identity = IDENTITY.exec(trimmed);
  if (identity) {
    return { type: "identity", field: identity[1] === "4" ? "serial" : "model", value: identity[2] ?? "", raw: line };
  }
  if (trimmed in ERRORS) return { type: "error", code: trimmed, message: ERRORS[trimmed] ?? trimmed, raw: line };
  if (ACKS.test(trimmed)) return { type: "ack", raw: line };
  return null;
}

export const mtSicsProtocol: BalanceProtocol = {
  id: "mt-sics",
  label: "Mettler Toledo MT-SICS (also Kern KCP)",
  parse: parseMtSicsLine,
  commands: {
    identify: ["I2", "I4"],
    startStream: "SIR",
    stopStream: "@",
    stable: "S",
    immediate: "SI",
    tare: "T",
    zero: "Z",
  },
  lineEnding: "\r\n",
  serial: { baudRate: 9600, dataBits: 8, parity: "none", stopBits: 1 },
};
